import {
  GitMergeIcon,
  GitPullRequestIcon,
  RepoForkedIcon,
  RepoPushIcon,
  StarIcon,
  type Icon,
} from '@primer/octicons-react'
import {RelativeTime} from '@primer/react'
import {Text, useTheme, useWindowSize} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useMemo} from 'react'

import {getLocale} from '@github-ui/swp-core/lib/utils/localization'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import {PlayButton} from '../../../../../brand/components/PlayButton/PlayButton'
import {useAnimatedMetrics} from '../../../hooks/use-animated-metrics'
import type {Metric} from '../../../Index.types'

import {useDataMarqueeMotion} from './DataMarquee.motion'

import Styles from './DataMarquee.module.css'

export type DataMarqueeEventType =
  | 'pull_request_opened'
  | 'pull_request_merged'
  | 'repository_forked'
  | 'repository_starred'
  | 'push'

export interface DataMarqueeEvent {
  type: 'event'
  data: {
    type: DataMarqueeEventType
    actorLogin: string
    repositoryNameWithOwner: string
    timestamp: string
    url: string
  }
}

export interface DataMarqueeStat {
  type: 'stat'
  data: Metric
}

export type DataMarqueeEventData = Omit<DataMarqueeEvent, 'type'>
export type DataMarqueeStatData = Omit<DataMarqueeStat, 'type'>

export interface DataMarqueeProps {
  items: Array<DataMarqueeEvent | DataMarqueeStat>
  copy: {
    events: {
      labelTemplate: Record<DataMarqueeEventType, {action: string; text: string}>
    }
  }
  a11y: {
    title: string
    play: string
    pause: string
  }
}

export const defaultProps: Partial<DataMarqueeProps> = {}

const EVENT_ICONS = {
  pull_request_opened: GitPullRequestIcon,
  pull_request_merged: GitMergeIcon,
  repository_forked: RepoForkedIcon,
  repository_starred: StarIcon,
  push: RepoPushIcon,
} satisfies Record<DataMarqueeEventType, Icon>

export const DataMarquee = (props: DataMarqueeProps) => {
  const initializedProps: DataMarqueeProps = {...defaultProps, ...props}
  const {items, copy, a11y} = initializedProps
  const {colorMode} = useTheme()
  const {isSmall: isTablet} = useWindowSize()
  const prefersReducedMotion = usePrefersReducedMotion()

  const stats = useMemo(() => props.items.flatMap(item => (item.type === 'stat' ? [item.data] : [])), [props.items])

  const {viewportRef, trackRef, orderedItems, isPaused, togglePlayback} = useDataMarqueeMotion(items)
  const getCounterRef = useAnimatedMetrics(stats, getLocale(), prefersReducedMotion)

  const getEventBlock = ({data}: DataMarqueeEvent) => {
    const EventIcon = EVENT_ICONS[data.type]
    const {action, text} = copy.events.labelTemplate[data.type]
    const labelValues = {
      '{action}': action,
      '{actorLogin}': data.actorLogin,
      '{repositoryNameWithOwner}': data.repositoryNameWithOwner,
    }

    return (
      <a className={Styles['DataMarquee__event']} href={data.url}>
        <Text size="100" className={Styles['DataMarquee__event_label']}>
          <span className={Styles['DataMarquee__event_icon']} aria-hidden="true">
            <EventIcon size={16} />
          </span>
          <Text size="100" variant="muted" className={Styles['DataMarquee__event_label_text']}>
            {text.split(/(\{action\}|\{actorLogin\}|\{repositoryNameWithOwner\})/).map((part, index) => {
              if (part !== '{action}' && part !== '{actorLogin}' && part !== '{repositoryNameWithOwner}') return part

              return (
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <Text size="100" className={Styles['DataMarquee__event_label_text']} key={index}>
                  {labelValues[part]}
                </Text>
              )
            })}
          </Text>
        </Text>
        <Text size="100" className={Styles['DataMarquee__event_details']}>
          <span>{data.repositoryNameWithOwner}</span>
          <Text size="100" variant="muted">
            <span className={Styles['DataMarquee__event_separator']} aria-hidden="true">
              {' · '}
            </span>
            <RelativeTime
              datetime={data.timestamp}
              format="relative"
              formatStyle={isTablet ? 'long' : 'short'}
              tense="past"
              precision="minute"
              noTitle
            />
          </Text>
        </Text>
      </a>
    )
  }

  const getStatBlock = ({data}: DataMarqueeStat) => (
    <div className={Styles['DataMarquee__stat']}>
      <Text size="500" font="monospace" hasAntiAliasing={false} className={Styles['DataMarquee__stat_value']}>
        <span>{data.value}</span>
        <span ref={getCounterRef(data)} aria-hidden="true">
          {data.value}
        </span>
      </Text>
      <Text size="100" font="monospace" variant="muted">
        {data.label}
      </Text>
    </div>
  )

  if (items.length === 0) return null

  return (
    <div className={clsx(Styles['DataMarquee'], colorMode === 'dark' && Styles['DataMarquee--dark'])}>
      <h2 className="visually-hidden">{a11y.title}</h2>
      <div className={Styles['DataMarquee__viewport']} ref={viewportRef}>
        <ul className={Styles['DataMarquee__track']} ref={trackRef}>
          {orderedItems.map(({item, key}, index) => (
            <li
              className={Styles['DataMarquee__block']}
              key={key}
              aria-hidden={index >= items.length || undefined}
              inert={index >= items.length}
            >
              {item.type === 'event' ? getEventBlock(item) : getStatBlock(item)}
            </li>
          ))}
        </ul>
      </div>
      <div className={Styles['DataMarquee__control']}>
        <PlayButton
          action={isPaused ? 'play' : 'pause'}
          size={isTablet ? undefined : 'small'}
          onClick={togglePlayback}
          label={isPaused ? a11y.play : a11y.pause}
        />
      </div>
    </div>
  )
}
